import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/db7jfg0mkl3spn8sm9ag";

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("感谢你的反馈，我们已经收到。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(responseText);

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("感谢你的反馈，我们已经收到。");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(serverMsg || "提交失败，请稍后重试。");
      }
    } catch (error) {
      setStatus("error");
      setFeedback(error instanceof Error ? error.message : "网络异常，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      id="feedback-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block font-label text-sm text-foreground-700">
            称呼（选填）
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            placeholder="怎么称呼你"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 font-label text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block font-label text-sm text-foreground-700">
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 font-label text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div>
        <label htmlFor="fb-message" className="mb-1.5 block font-label text-sm text-foreground-700">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="告诉我们你的建议或想看到的内容（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm leading-relaxed text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-honeypot-field"
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {status === "submitting" ? (
          <>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-base"></i>
            </span>
            提交中
          </>
        ) : (
          <>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-send-plane-line text-base"></i>
            </span>
            提交反馈
          </>
        )}
      </button>

      {feedback && (
        <p
          role="status"
          className={`rounded-md px-4 py-3 text-sm ${
            status === "success"
              ? "bg-primary-50 text-primary-700"
              : "bg-accent-50 text-accent-800"
          }`}
        >
          {feedback}
        </p>
      )}
    </form>
  );
}